import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useDispatch, useSelector } from 'react-redux';
import {
  addCollectionStatus,
  addCollectionError,
  editCollectionStatus,
  editCollectionError,
  selectedCollectionDetails,
} from '../store/collection.selectors';
import {
  addCollection,
  fetchAllCollections,
  editCollection,
  resetSnackbarDetails,
} from '../store/collection.actions';
import { selectedCompanyDetails, selectedCompanyID } from '../../company/store/company.selectors';
import PropTypes from 'prop-types';
import {
  fetchCompanyUsers,
  fetchCollectionUsers,
  assignCollectionToUser,
  deleteUserFromCollection,
  resetAlert,
} from '../../user/store/user.actions';
import {
  collectionUsers,
  addUserCollectionLoading,
  addUserCollectionError,
} from '../../user/store/user.selectors';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Delete from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Close,
  Business,
  Edit,
  WorkOutline,
  Work,
  Person,
  PersonOutline,
} from '@mui/icons-material';
import {
  StyledModal,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  StyledCloseButton,
  ModalContent,
  StyledTextField,
  StyledFormControl,
  StyledSelect,
  PrimaryButton,
  FormSection,
  StyledDialog,
  DialogHeaderStyled,
  UserListContainer,
  UserListItem,
  DeleteButton,
  AddUserButton,
} from '../../../utils/common/styles/commonStyles';

const AddCollectionModal = ({ open, handleClose }) => {
  const dispatch = useDispatch();
  const selectedComp = useSelector(selectedCompanyDetails);
  const newCollectionStatus = useSelector(addCollectionStatus);
  const newCollectionError = useSelector(addCollectionError);
  const editSelectedCollection = useSelector(selectedCollectionDetails);
  const edtCollStatus = useSelector(editCollectionStatus);
  const edtCollError = useSelector(editCollectionError);
  const selCompID = useSelector(selectedCompanyID);
  const jbUsers = useSelector(collectionUsers);
  const addJbUsrError = useSelector(addUserCollectionError);
  const addJbUsrLoading = useSelector(addUserCollectionLoading);

  const initialFormData = {
    name: '',
    description: '',
    companyID: '',
    companyName: '',
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState(initialFormData);
  const [originalFormData, setOriginalFormData] = useState(initialFormData);
  const [isEdit, setIsEdit] = useState(false);
  const [isUsersListOpen, setIsUsersListOpen] = useState(false);
  const filteredList = [];
  const [selectedUser, setSelectedUser] = useState();
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && !editSelectedCollection) {
      setFormData((prev) => ({
        ...prev,
        companyID: selectedComp.id,
        companyName: selectedComp.companyName,
      }));
    } else if (open && editSelectedCollection) {
      setIsEdit(true);
      const initialEditForm = {
        id: editSelectedCollection.id,
        name: editSelectedCollection.name || editSelectedCollection.collectionName,
        description: editSelectedCollection.description || '',
        companyID: editSelectedCollection.companyID,
        companyName: selectedComp.companyName,
      };
      setFormData(initialEditForm);
      setOriginalFormData(initialEditForm);
      dispatch(fetchCompanyUsers(editSelectedCollection.companyID));
      dispatch(fetchCollectionUsers(editSelectedCollection.id));
    }
  }, [open, selectedComp, editSelectedCollection]);

  useEffect(() => {
    if (!open) {
      setFormData(initialFormData);
      setIsEdit(false);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (newCollectionStatus === 'success' || edtCollStatus === 'success') {
      setSnackbar({
        open: true,
        message: isEdit ? 'Collection updated successfully!' : 'Collection created successfully!',
        severity: 'success',
      });
      dispatch(fetchAllCollections(selCompID));
      handleClose();
      setFormData(initialFormData);
      setIsLoading(false);
    } else if (
      (newCollectionStatus === 'fail' && newCollectionError) ||
      (edtCollStatus === 'fail' && edtCollError)
    ) {
      setSnackbar({
        open: true,
        message: isEdit ? edtCollError : newCollectionError,
        severity: 'error',
      });
    }
    dispatch(resetSnackbarDetails());
  }, [newCollectionStatus, newCollectionError, edtCollStatus, edtCollError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectUser = (e) => {
    setSelectedUser(e.target.value);
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (isEdit) dispatch(editCollection({ ...formData, id: editSelectedCollection.id }));
    else dispatch(addCollection(formData));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleUsersClose = (e, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    setSelectedUser();
    setSuccessAlert(false);
    setErrorAlert(false);
    dispatch(resetAlert());
    setIsUsersListOpen(false);
  };

  const handleAddUserToCollection = async () => {
    const data = { collectionID: editSelectedCollection.id, userID: selectedUser.id };
    await dispatch(assignCollectionToUser(data));
    if (!addJbUsrError && !addJbUsrLoading) setSuccessAlert(true);
    if (addJbUsrError) setErrorAlert(true);
    dispatch(fetchCollectionUsers(editSelectedCollection.id));
    setSelectedUser();
  };

  const handleRemoveUser = async (user) => {
    const data = { collectionID: user.collectionId, userID: user.userId };
    const response = await dispatch(deleteUserFromCollection(data));
    if (response.status === 'success') {
      await dispatch(fetchCollectionUsers(editSelectedCollection.id));
    }
  };

  const UsersListDialog = () => (
    <StyledDialog
      open={isUsersListOpen}
      onClose={handleUsersClose}
      maxWidth="md"
      slotProps={{ paper: { sx: { minWidth: '550px' } } }}
    >
      <DialogHeaderStyled>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" gap={1}>
            <Person />
            User Management
          </Box>
          <StyledCloseButton onClick={handleUsersClose} size="small">
            <Close />
          </StyledCloseButton>
        </Box>
      </DialogHeaderStyled>

      <DialogContent sx={{ padding: 3 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: '#2c3e50',
            marginBottom: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WorkOutline color="action" />
          Collection Users
        </Typography>

        {jbUsers && jbUsers.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              '& .MuiAlert-icon': { color: '#667eea' },
              marginBottom: 3,
            }}
          >
            No users assigned to this collection yet
          </Alert>
        ) : (
          <UserListContainer>
            <List sx={{ padding: 1 }}>
              {jbUsers?.map((user) => (
                <UserListItem
                  key={user.userId}
                  disablePadding
                  secondaryAction={
                    <DeleteButton edge="end" size="small" onClick={() => handleRemoveUser(user)}>
                      <Delete fontSize="small" />
                    </DeleteButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#2c3e50',
                        }}
                      >
                        {user.userName}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{
                          fontSize: '0.775rem',
                          color: '#64748b',
                          fontWeight: 500,
                        }}
                      >
                        {user.companyName}
                      </Typography>
                    }
                  />
                </UserListItem>
              ))}
            </List>
          </UserListContainer>
        )}

        <Divider sx={{ my: 2, opacity: 0.5 }} />

        <Box mt={2}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: '#2c3e50',
              marginBottom: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PersonOutline color="action" />
            Assign New User
          </Typography>

          <StyledFormControl required fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Select a user</InputLabel>
            <StyledSelect
              name="addedUserId"
              value={selectedUser}
              onChange={handleSelectUser}
              label="Select a user"
              size="small"
            >
              {filteredList?.map((user) => (
                <MenuItem value={user} key={user.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonOutline fontSize="small" color="action" />
                    {user.name}
                  </Box>
                </MenuItem>
              ))}
            </StyledSelect>
          </StyledFormControl>

          {selectedUser && (
            <AddUserButton variant="contained" onClick={handleAddUserToCollection} size="small">
              Add User to Collection
            </AddUserButton>
          )}

          {successAlert && !addJbUsrError && (
            <Alert
              severity="success"
              sx={{
                borderRadius: 2,
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                '& .MuiAlert-icon': { color: '#667eea' },
              }}
            >
              User successfully assigned to the collection!
            </Alert>
          )}
          {errorAlert && addJbUsrError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {addJbUsrError}
            </Alert>
          )}
        </Box>
      </DialogContent>
    </StyledDialog>
  );

  const isFormValid = formData.name && formData.companyName;
  const isFormChanged =
    !isEdit ||
    formData.name !== originalFormData.name ||
    (formData.description || '') !== (originalFormData.description || '') ||
    formData.companyID !== originalFormData.companyID ||
    formData.companyName !== originalFormData.companyName;

  return (
    <>
      <StyledModal open={open} onClose={handleClose}>
        <Fade in={open} timeout={300}>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>
                {isEdit ? <Edit /> : <Work />}
                {isEdit ? 'Edit Collection' : 'Add New Collection'}
              </ModalTitle>
              <StyledCloseButton
                onClick={() => {
                  setFormData(initialFormData);
                  handleClose();
                }}
                size="small"
              >
                <Close />
              </StyledCloseButton>
            </ModalHeader>

            <ModalContent>
              <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <FormSection>
                  <StyledTextField
                    required
                    label="Collection Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter collection name"
                    fullWidth
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkOutline color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormSection>

                <FormSection>
                  <StyledTextField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter collection description (optional)"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                  />
                </FormSection>

                <FormSection>
                  <StyledTextField
                    required
                    disabled
                    label="Company Name"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter company name"
                    fullWidth
                    size="small"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormSection>

                <Box
                  sx={{ display: 'flex', justifyContent: isEdit ? 'space-between' : 'flex-end' }}
                >
                  <PrimaryButton
                    onClick={handleSubmit}
                    disabled={!isFormValid || isLoading || !isFormChanged}
                  >
                    {isLoading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={18} color="inherit" />
                        <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
                      </Box>
                    ) : (
                      <>{isEdit ? 'Update Collection' : 'Create Collection'}</>
                    )}
                  </PrimaryButton>
                </Box>
              </Box>
            </ModalContent>
          </ModalContainer>
        </Fade>
      </StyledModal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <UsersListDialog />
    </>
  );
};

export default AddCollectionModal;

AddCollectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};
